import React from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { DesktopIcon, MobileIcon, TabletIcon } from '../../../../svg';
import useI18n from '../../../../hooks/useI18n';
import { formatPercent } from '@/utils/numberUtils';

interface DeviceTypeChartProps {
  deviceData: {
    mobile: number;
    tablet: number;
    pc: number;
  };
}

const DeviceTypeChart: React.FC<DeviceTypeChartProps> = ({ deviceData }) => {
  const { t } = useI18n();
  const series = [deviceData.mobile, deviceData.tablet, deviceData.pc];
  const labels = [
    String(t('analytics.mobile')),
    String(t('analytics.tablet')),
    String(t('analytics.desktop')),
  ];
  const colors = ['#FCDCB9', '#B8F9CF', '#F3C6E2'];

  // Custom Icons Mapping
  const icons: Record<string, React.ReactNode> = {
    [String(t('analytics.mobile'))]: (
      <MobileIcon className="size-5 lg:size-6" />
    ),
    [String(t('analytics.tablet'))]: (
      <TabletIcon className="size-5 lg:size-6" />
    ),
    [String(t('analytics.desktop'))]: (
      <DesktopIcon className="size-5 lg:size-6" />
    ),
  };

  const options: ApexOptions = {
    chart: {
      type: 'donut',
      offsetY: 0,
    },
    colors,
    labels,
    legend: {
      show: false, // Hide the default legend
    },
    plotOptions: {
      pie: {
        donut: {
          size: '55%', //  for a thicker donut ring
        },
        offsetY: 0,
      },
    },
    tooltip: {
      enabled: false, // Disable the tooltip
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => formatPercent(val),
      style: {
        colors: ['#383838'],
        fontSize: '16px',
      },
      dropShadow: {
        enabled: false,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    stroke: {
      width: 4,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          dataLabels: {
            style: {
              fontSize: '7px',
            },
          },
        },
      },
    ],
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ maxHeight: '60vh', height: '100%', position: 'relative' }}
    >
      <div className="flex-grow flex items-center justify-between px-4 lg:px-6 py-4">
        <div
          className="flex items-center justify-center"
          style={{ width: '100%', height: '100%' }}
        >
          <ReactApexChart
            options={options}
            series={series}
            type="donut"
            height="350"
          />
        </div>

        {/* Custom Legend */}
        <div className="flex flex-col space-y-2 lg:pr-12">
          {labels.map((label, index) => (
            <div key={label} className="flex items-center justify-between">
              <div className="flex items-center space-x-2 space-y-2">
                <div
                  className="w-6 h-6 lg:w-7 lg:h-7 rounded-[3px] mt-2"
                  style={{ backgroundColor: colors[index] }}
                />
                <span className="text-[#292828] font-medium text-sm lg:text-[20px]">
                  {label}
                </span>
              </div>

              {/* Icon on the right */}
              <div className="ml-4 pt-2">{icons[label]}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeviceTypeChart;
